import React, { useState, useEffect } from 'react';
import { FiSave, FiTrash2, FiAlertTriangle, FiMessageSquare, FiEdit3, FiBold, FiItalic, FiUnderline, FiEye, FiSend } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import toast from 'react-hot-toast';
import RichTextEditor from '../common/RichTextEditor';
import ConfirmationModal from '../common/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';

export default function NewsManagement() {
  const { currentTenant } = useTenant();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel: handleConfirmationCancel } = useConfirmation();
  const [activeMessage, setActiveMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    expiryDate: '',
    isEmergency: false
  });
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadActiveMessage();
  }, [currentTenant]);

  const loadActiveMessage = async () => {
    if (!currentTenant?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      tenantDataService.setTenantContext(currentTenant.id);
      const message = await tenantDataService.getById('news', 'active_message');
      if (message) {
        setActiveMessage(message);
        setFormData({
          title: message.title || '',
          content: message.content || '',
          expiryDate: message.expiryDate || '',
          isEmergency: message.isEmergency || false
        });
      }
    } catch (error) {
      console.error('Error loading team message:', error);
      toast.error('Failed to load team message');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    try {
      tenantDataService.setTenantContext(currentTenant.id);
      const message = {
        id: activeMessage?.id || Date.now().toString(),
        ...formData,
        createdAt: activeMessage?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: true
      };

      // Use create if no active message exists, otherwise update
      if (activeMessage) {
        await tenantDataService.update('news', 'active_message', message);
      } else {
        await tenantDataService.create('news', { ...message, id: 'active_message' });
      }
      setActiveMessage(message);
      setIsEditing(false);
      toast.success('Team message saved successfully');
      
      // Trigger event for other components to check for new messages
      window.dispatchEvent(new Event('team-message-updated'));
    } catch (error) {
      console.error('Error saving team message:', error);
      toast.error('Failed to save team message');
    }
  };

  const handleClear = async () => {
    const confirmed = await showConfirmation({
      title: 'Clear Team Message',
      message: 'Are you sure you want to clear the current team message?',
      confirmText: 'Clear',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;

    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    try {
      tenantDataService.setTenantContext(currentTenant.id);
      await tenantDataService.delete('news', 'active_message');
      setActiveMessage(null);
      setFormData({
        title: '',
        content: '',
        expiryDate: '',
        isEmergency: false
      });
      toast.success('Team message cleared');
      window.dispatchEvent(new Event('team-message-updated'));
    } catch (error) {
      console.error('Error clearing team message:', error);
      toast.error('Failed to clear team message');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setPreviewMode(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (activeMessage) {
      setFormData({
        title: activeMessage.title || '',
        content: activeMessage.content || '',
        expiryDate: activeMessage.expiryDate || '',
        isEmergency: activeMessage.isEmergency || false
      });
    }
  };

  const formatText = (type) => {
    const textarea = document.getElementById('message-content');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    if (!selectedText) return;

    let formattedText = '';
    switch (type) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        return;
    }

    const newContent = 
      formData.content.substring(0, start) + 
      formattedText + 
      formData.content.substring(end);
    
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const renderFormattedContent = (content) => {
    if (!content) return '';
    
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<u>$1</u>')
      .replace(/\n/g, '<br>');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading team messages...</div>
      </div>
    );
  }

  if (!currentTenant) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">No clinic selected</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team News & Announcements</h2>
          <div className="text-sm text-gray-600 mt-1">
            Team messages for: <strong>{currentTenant.name}</strong>
          </div>
        </div>
        {activeMessage && !isEditing && (
          <div className="flex space-x-3">
            <button
              onClick={handleEdit}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit3 size={16} />
              <span>Edit Message</span>
            </button>
            <button
              onClick={handleClear}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiTrash2 size={16} />
              <span>Clear Message</span>
            </button>
          </div>
        )}
      </div>

      {/* Current Active Message */}
      {activeMessage && !isEditing && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Current Active Message</h3>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              {activeMessage.isEmergency && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                  Emergency
                </span>
              )}
              <span>Created: {new Date(activeMessage.createdAt).toLocaleDateString()}</span>
              {activeMessage.expiryDate && (
                <span>Expires: {new Date(activeMessage.expiryDate).toLocaleDateString()}</span>
              )}
            </div>
          </div>
          
          {activeMessage.title && (
            <h4 className="text-xl font-bold text-gray-900 mb-3">{activeMessage.title}</h4>
          )}
          
          <div 
            className="prose max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: renderFormattedContent(activeMessage.content) }}
          />
        </div>
      )}

      {/* Message Editor */}
      {(isEditing || !activeMessage) && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {activeMessage ? 'Edit Team Message' : 'Create Team Message'}
          </h3>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Title (Optional)
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter message title..."
              />
            </div>

            {/* Content Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Content
                </label>
                <div className="flex items-center space-x-2">
                  {/* Formatting Buttons */}
                  <button
                    type="button"
                    onClick={() => formatText('bold')}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Bold"
                  >
                    <FiBold size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('italic')}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Italic"
                  >
                    <FiItalic size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => formatText('underline')}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Underline"
                  >
                    <FiUnderline size={16} />
                  </button>
                  <div className="border-l border-gray-300 h-6 mx-2"></div>
                  <button
                    type="button"
                    onClick={() => setPreviewMode(!previewMode)}
                    className={`flex items-center space-x-1 px-3 py-1 rounded transition-colors ${
                      previewMode 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FiEye size={16} />
                    <span className="text-sm">Preview</span>
                  </button>
                </div>
              </div>

              {previewMode ? (
                <div className="min-h-[200px] border border-gray-300 rounded-lg p-3 bg-gray-50">
                  {formData.title && (
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{formData.title}</h4>
                  )}
                  <div 
                    className="prose max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: renderFormattedContent(formData.content) }}
                  />
                </div>
              ) : (
                <textarea
                  id="message-content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={8}
                  placeholder="Enter your team message here... Use **bold**, *italic*, and __underline__ for formatting."
                  required
                />
              )}
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center space-x-3 pt-6">
                <input
                  type="checkbox"
                  id="emergency"
                  checked={formData.isEmergency}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEmergency: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="emergency" className="text-sm font-medium text-gray-700">
                  Emergency Message (shows on every login)
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              {isEditing && (
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!formData.content.trim()}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSend size={16} />
                <span>Issue Team Message</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">How Team Messages Work</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Team messages are shown to all non-admin staff when they log in</li>
          <li>• Only one message can be active at a time</li>
          <li>• Emergency messages are shown on every login until cleared</li>
          <li>• Regular messages are shown once per session</li>
          <li>• Messages automatically expire on the set date</li>
          <li>• Use **bold**, *italic*, and __underline__ for text formatting</li>
        </ul>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleConfirmationCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </div>
  );
}
